import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzAlgDataService,
  BokkzVpbDataService,
  BokkzVpbLogic,
  CorporateTaxEntity,
  KolommenbalansService,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

const ELIMINATIE_NAAM = 'Eliminatie';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-samenstelling',
  templateUrl: './fiscale-eenheid-samenstelling.component.html',
  styleUrls: ['./fiscale-eenheid-samenstelling.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class FiscaleEenheidSamenstellingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzDataService = inject(BokkzAlgDataService);
  private kolommenbalansService = inject(KolommenbalansService);
  public vpbService = inject(BokkzVpbLogic);

  public dialogVisible = false;
  public options: SelectFormControlItem<string>[] = [];
  private clientId = '';
  private boekjaarBegindatum: Date | undefined;
  private boekjaarEinddatum: Date | undefined;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: false, label: 'Moeder'},
    {value: true, label: 'Dochter'},
  ];

  public enkelvoudigFg = new FormGroup({
    enkelvoudigecijfers: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(data => {
        if (!data.aangifte) return;
        this.boekjaarBegindatum = data.aangifte.boekjaar?.begindatum;
        this.boekjaarEinddatum = data.aangifte.boekjaar?.einddatum;
        this.syncFormArray(data.aangifte);
      }
    );
    this.bokkzDataService.options.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.options = value;
    });
    this.enkelvoudigFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  get enkelvoudigecijfers(): FormArray {
    return this.enkelvoudigFg.get('enkelvoudigecijfers') as FormArray;
  }

  addRij(): void {
    this.enkelvoudigecijfers.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.enkelvoudigecijfers.removeAt(index, {emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: BalansEnResultatenrekening, name?: string): FormGroup {
    return new FormGroup({
      naamVennootschap: new FormControl<string | null>(item ? item.naamVennootschap : name ?? null),
      boekjaarVennootschap: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.begindatum ?? this.boekjaarBegindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.einddatum ?? this.boekjaarEinddatum ?? null),
      }),
      fiscaalNummer: new FormControl<string | null>(item?.fiscaalNummer ?? null),
      omschrijvingActiviteiten: new FormControl<string | null>(item?.omschrijvingActiviteiten ?? 'Activiteiten'),
      agrarischeActiviteiten: new FormControl<boolean | null>(item?.agrarischeActiviteiten ?? false),
      dochtermaatschappijFiscaleEenheid: new FormControl<boolean | null>(item?.dochtermaatschappijFiscaleEenheid ?? true),
      administratieId: new FormControl<string | null>(item?.administratieId ?? null),
    });
  }

  getAdministration(i: number): void {
    const vennootschap = this.enkelvoudigecijfers.at(i);
    this.kolommenbalansService.getUpdatedAangifteMetAdministratie(
      vennootschap.value.administratieId,
      this.clientId,
      {} as CorporateTaxEntity,
      false,
      this.clientId
    ).then((entity) => {
      if (entity.aangifte?.identificatie) {
      }
    });
  }

  private syncFormArray(aangifte: VpbAangifte): void {
    const lijst = this.getRowsForForm(aangifte);
    this.applyRowsToFormArray(lijst);
  }

  private getRowsForForm(aangifte: VpbAangifte): BalansEnResultatenrekening[] {
    const raw = [...(aangifte.enkelvoudigeResultatenFiscaleEenheid ?? [])];
    const nonEliminatieRows = raw.filter(row => row.naamVennootschap !== ELIMINATIE_NAAM);
    const eliminatieRow = raw.find(row => row.naamVennootschap === ELIMINATIE_NAAM);

    const parentIndex = nonEliminatieRows.findIndex(row => !row.dochtermaatschappijFiscaleEenheid);
    const parentRow = parentIndex >= 0
      ? nonEliminatieRows.splice(parentIndex, 1)[0]
      : this.createFallbackParentRow(aangifte);

    return [
      parentRow,
      ...nonEliminatieRows,
      eliminatieRow ?? ({naamVennootschap: ELIMINATIE_NAAM} as BalansEnResultatenrekening),
    ];
  }

  private getRowMergeKey(row: Partial<BalansEnResultatenrekening>): string {
    if (row.naamVennootschap === ELIMINATIE_NAAM) return 'eliminatie';
    if (row.administratieId) return `admin:${row.administratieId}`;
    return `name:${row.naamVennootschap ?? ''}|dochter:${row.dochtermaatschappijFiscaleEenheid ?? ''}`;
  }

  private mergeRowsWithExistingData(
    formRows: Partial<BalansEnResultatenrekening>[],
    existingRows: BalansEnResultatenrekening[]
  ): BalansEnResultatenrekening[] {
    const buckets = new Map<string, BalansEnResultatenrekening[]>();
    for (const existingRow of existingRows) {
      const key = this.getRowMergeKey(existingRow);
      const values = buckets.get(key) ?? [];
      values.push(existingRow);
      buckets.set(key, values);
    }

    return formRows.map((formRow) => {
      const key = this.getRowMergeKey(formRow);
      const candidates = buckets.get(key);
      const existing = candidates?.shift();
      return {
        ...existing,
        ...formRow,
      } as BalansEnResultatenrekening;
    });
  }

  private createFallbackParentRow(aangifte: VpbAangifte): BalansEnResultatenrekening {
    return {
      ...aangifte.balansEnResultatenrekening,
      naamVennootschap:
        aangifte.balansEnResultatenrekening?.naamVennootschap ??
        aangifte.identificatie?.naamLichaam,
      dochtermaatschappijFiscaleEenheid: false,
    } as BalansEnResultatenrekening;
  }

  private applyRowsToFormArray(rows: BalansEnResultatenrekening[]): void {
    const formArray = this.enkelvoudigecijfers;
    while (formArray.length > rows.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < rows.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(rows[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(rows[i]), {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.enkelvoudigFg.updateValueAndValidity({emitEvent: false});
    const formRows = this.enkelvoudigecijfers.getRawValue() as Partial<BalansEnResultatenrekening>[];

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      const existingRows = this.getRowsForForm(currentEntity.aangifte);
      const enkelvoudigeResultatenFiscaleEenheid = this.mergeRowsWithExistingData(formRows, existingRows);
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          enkelvoudigeResultatenFiscaleEenheid,
        } as VpbAangifte,
      };
    });
  }

  protected readonly filter = filter;
}
